import type { ReactNode } from "react";
import ToolResultBadge from "@/components/tools/ToolResultBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const POSITIVE_LABELS = new Set(["Yes", "نعم"]);
const NEGATIVE_LABELS = new Set(["No", "كلا"]);

export type ToolResultSection = {
  title: string;
  rows: { label: string; value: ReactNode }[];
};

function ResultValue({ value }: { value: ReactNode }) {
  if (typeof value !== "string" && typeof value !== "number") {
    return value;
  }

  const text = String(value);

  if (POSITIVE_LABELS.has(text)) {
    return (
      <ToolResultBadge className="border-[color-mix(in_srgb,var(--ok)_22%,transparent)] bg-[color-mix(in_srgb,var(--ok)_12%,white)] font-medium text-[#0a6234]">
        {text}
      </ToolResultBadge>
    );
  }

  if (NEGATIVE_LABELS.has(text)) {
    return (
      <ToolResultBadge className="border-foreground/10 bg-foreground/[0.08] font-medium text-foreground/80">
        {text}
      </ToolResultBadge>
    );
  }

  if (text === "N/A") {
    return <span className="text-sm text-muted-foreground">{text}</span>;
  }

  return (
    <span className="text-sm font-medium text-foreground [overflow-wrap:anywhere]">
      {text}
    </span>
  );
}

function ResultRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3.5">
      <dt className="shrink-0 text-sm text-foreground/70">{label}</dt>
      <dd className="min-w-0 text-end">
        <ResultValue value={value} />
      </dd>
    </div>
  );
}

export default function ToolResultTable({
  sections,
}: {
  sections: ToolResultSection[];
}) {
  return (
    <div className="flex w-full flex-col gap-5">
      {sections.map((section) => (
        <Card
          key={section.title}
          className="w-full gap-0 rounded-2xl border border-line bg-white py-0 shadow-[0_1px_2px_rgb(15_23_42/0.04)] ring-0"
        >
          <CardHeader className="border-b border-foreground/6 bg-foreground/[0.02] px-5 py-4">
            <CardTitle className="text-base font-semibold tracking-tight text-foreground">
              {section.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 py-1">
            <dl className="divide-y divide-foreground/6">
              {section.rows.map((row) => (
                <ResultRow
                  key={row.label}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </dl>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
